import { ui } from "angular";
import * as echarts from "echarts";
import { BAR_CHART_COLORS, BAR_CHART_OPTIONS } from "@constants/reporting";
import { AssetClassNames } from "@common/enums/AssetClassType";
import { Locality } from "@services/cmodel.repo";

export class TopHoldings {
    public allPortfolioHoldings: any[] = []
    //top holdings over total portfolio
    public topCombinedHoldings: boolean;
    public topCombinedEquityHoldings: boolean;
    public topCombinedFixedIncomeHoldings: boolean;

    //top holdings over total portfolio
    public topLocalHoldings: boolean;
    public topLocalEquityHoldings: boolean;
    public topLocalFixedIncomeHoldings: boolean;

    //top holdings over total portfolio
    public topInternationalHoldings: boolean;
    public topInternationalEquityHoldings: boolean;
    public topInternationalFixedIncomeHoldings: boolean;

      //top holdings over total portfolio Charts
    public topCombinedHoldingsChart: any;
    public topCombinedEquityHoldingsChart: any;
    public topCombinedFixedIncomeHoldingsChart: any;

    //top holdings over total portfolio Charts
    public topLocalHoldingsChart: any;
    public topLocalEquityHoldingsChart: any;
    public topLocalFixedIncomeHoldingsChart: any;

    //top holdings over total portfolio Charts
    public topInternationalHoldingsChart: any;
    public topInternationalEquityHoldingsChart: any;
    public topInternationalFixedIncomeHoldingsChart: any;

    public localPorfolioValue: number = 0;
    public internationalPortfolioValue: number = 0;

    setLocalPortfolioValue(localValue: number) {
        this.localPorfolioValue = localValue
    }

    setInternationalPortfolioValue(internationalValue: number) {
        this.internationalPortfolioValue = internationalValue
    }
    setTotalPortfolio(allPortfolios: any[]) {
        this.allPortfolioHoldings = allPortfolios
    }

    initializeCharts() {
        //OVER TOTAL PORTFOLIO
        this.topCombinedHoldingsChart = echarts.init(
            document.getElementById("top-combined-holdings-bar-chart")
        );
        this.topCombinedEquityHoldingsChart = echarts.init(
            document.getElementById("top-combined-equity-holdings-bar-chart")
        );
        this.topCombinedFixedIncomeHoldingsChart = echarts.init(
            document.getElementById("top-combined-fixed-income-holdings-bar-chart")
        );
        //OVER  LOCAL PORTFOLIO
        this.topLocalHoldingsChart = echarts.init(
            document.getElementById("top-local-holdings-bar-chart")
        );
        this.topLocalEquityHoldingsChart = echarts.init(
            document.getElementById("top-local-equity-holdings-bar-chart")
        );
        this.topLocalFixedIncomeHoldingsChart = echarts.init(
            document.getElementById("top-local-fixed-income-holdings-bar-chart")
        );
        //OVER INTERNATIONAL PORTFOLIO
        this.topInternationalHoldingsChart = echarts.init(
            document.getElementById("top-international-holdings-bar-chart")
        );
        this.topInternationalEquityHoldingsChart = echarts.init(
            document.getElementById("top-international-equity-holdings-bar-chart")
        );
        this.topInternationalFixedIncomeHoldingsChart = echarts.init(
            document.getElementById("top-international-fixed-income-holdings-bar-chart")
        );
    }

    setChartDisplayToggleValues(stateParams: ui.IStateParamsService) {
        //top holdings over total portfolio
        this.topCombinedHoldings = stateParams.topCombinedHoldings === "true";
        this.topCombinedEquityHoldings = stateParams.topCombinedEquityHoldings === "true";
        this.topCombinedFixedIncomeHoldings = stateParams.topCombinedFixedIncomeHoldings === "true";
        //top holdings over total portfolio
        this.topLocalHoldings = stateParams.topLocalHoldings === "true";
        this.topLocalEquityHoldings = stateParams.topLocalEquityHoldings === "true";
        this.topLocalFixedIncomeHoldings = stateParams.topLocalFixedIncomeHoldings === "true";
        //top holdings over total portfolio
        this.topInternationalHoldings = stateParams.topInternationalHoldings === "true";
        this.topInternationalEquityHoldings = stateParams.topInternationalEquityHoldings === "true";
        this.topInternationalFixedIncomeHoldings = stateParams.topInternationalFixedIncomeHoldings === "true";
    }

    /**
     * Starts by hiding graphs with no data, then builds
     * and sets chart options for top holding bar charts
     */
    setReportTopPortfolioCharts() {
        this.hideGraphsWithNoData()
        //top holdings over Total portfolio charts
        this.topCombinedHoldings && this.topCombinedHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions())
        this.topCombinedEquityHoldings && this.topCombinedEquityHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions(null, AssetClassNames.EQUITY))
        this.topCombinedFixedIncomeHoldings && this.topCombinedFixedIncomeHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions(null, AssetClassNames.FIXED_INCOME))
        //top holdings over Local portfolio charts
        this.topLocalHoldings && this.topLocalHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions(Locality.Local))
        this.topLocalEquityHoldings && this.topLocalEquityHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions(Locality.Local, AssetClassNames.EQUITY))
        this.topLocalFixedIncomeHoldings && this.topLocalFixedIncomeHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions(Locality.Local, AssetClassNames.FIXED_INCOME))
        //top holdings over International portfolio charts
        this.topInternationalHoldings && this.topInternationalHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions(Locality.International))
        this.topInternationalEquityHoldings && this.topInternationalEquityHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions(Locality.International, AssetClassNames.EQUITY))
        this.topInternationalFixedIncomeHoldings && this.topInternationalFixedIncomeHoldingsChart.setOption(this.setTopPortfoliosBarChartOptions(Locality.International, AssetClassNames.FIXED_INCOME))
    }

    /**
     * Builds and returns an echarts options object based on the passed parameters
     * @param locality The locality we want to generate bar chart options for. if null, treated as combined portfolio
     * @param assetClass The asset class we want to generate bar chart options for. If null, treated as combined portfolios
     * @returns An options object to build a bar chart based on. Includes colors, the data on x/y axis
     */
    public setTopPortfoliosBarChartOptions(locality: number = null, assetClass: string = null) {
        const combinedAssetClassesTotal = this.getAssetClassTotalByLocality(locality)
      
        let filteredPortFolios;
        //Filter out cash securities when building charts
        filteredPortFolios = this.allPortfolioHoldings.filter(p => p.assetClass !== AssetClassNames.CASH && p.marketValueDisplayCurrency > 0);
        filteredPortFolios = filteredPortFolios.filter(p => ((locality === null || p.locality === locality) && (assetClass === null || p.assetClass === assetClass) && p.marketValueDisplayCurrency > 0));
        //top holdings over total portfolio
        const top5Holdings = filteredPortFolios.length > 5 ? filteredPortFolios.slice(filteredPortFolios.length-5, filteredPortFolios.length) : filteredPortFolios
        return {
            ...BAR_CHART_OPTIONS,
            yAxis: {
                ...BAR_CHART_OPTIONS.yAxis,
                data: top5Holdings.map(holding => this.formatLongNames(holding[this.getPropertyNameByAssetClass(holding.assetClass)].name))
            },
            series: [
                {
                    ...BAR_CHART_OPTIONS.series[0],
                    data: top5Holdings.map((holding, index) => {
                                return {
                                    value: Number(((holding.marketValueDisplayCurrency/(combinedAssetClassesTotal))*100)).toFixed(2),
                                    itemStyle: {
                                        color: BAR_CHART_COLORS[index]
                                    }
                                }   
                            })
                }
            ]
        }
    }

    /**
     * Hides or shows top holdings graphs based on 
     * a boolean that determines whether it should be shown
     */
    public hideGraphsWithNoData() {
        //top holdings over Total portfolio charts
        this.topCombinedHoldings  = this.topCombinedHoldings && this.setTopPortfoliosBarChartOptions().series[0].data.length > 0;
        this.topCombinedEquityHoldings= this.topCombinedEquityHoldings && this.setTopPortfoliosBarChartOptions(null, AssetClassNames.EQUITY).series[0].data.length > 0
        this.topCombinedFixedIncomeHoldings = this.topCombinedFixedIncomeHoldings && this.setTopPortfoliosBarChartOptions(null, AssetClassNames.FIXED_INCOME).series[0].data.length > 0
        //top holdings over Local portfolio charts
        this.topLocalHoldings = this.topLocalHoldings && this.setTopPortfoliosBarChartOptions(Locality.Local).series[0].data.length > 0
        this.topLocalEquityHoldings = this.topLocalEquityHoldings && this.setTopPortfoliosBarChartOptions(Locality.Local, AssetClassNames.EQUITY).series[0].data.length > 0
        this.topLocalFixedIncomeHoldings = this.topLocalFixedIncomeHoldings && this.setTopPortfoliosBarChartOptions(Locality.Local, AssetClassNames.FIXED_INCOME).series[0].data.length > 0
        //top holdings over International portfolio charts
        this.topInternationalHoldings = this.topInternationalHoldings && this.setTopPortfoliosBarChartOptions(Locality.International).series[0].data.length > 0
        this.topInternationalEquityHoldings = this.topInternationalEquityHoldings && this.setTopPortfoliosBarChartOptions(Locality.International, AssetClassNames.EQUITY).series[0].data.length > 0
        this.topInternationalFixedIncomeHoldings = this.topInternationalFixedIncomeHoldings && this.setTopPortfoliosBarChartOptions(Locality.International, AssetClassNames.FIXED_INCOME).series[0].data.length > 0
    }

    /**
     * 
     * @param assetClass The asset class name
     * @returns The object property name of the asset class. Each asset class has a unique name for the property
     */
    public getPropertyNameByAssetClass(assetClass: string) {
        switch(assetClass) {
          case AssetClassNames.EQUITY:
            return 'equityItem'
          case AssetClassNames.FIXED_INCOME:
            return 'fixedIncomeItem'
          case AssetClassNames.ALTERNATIVE_INV:
            return 'alternativeInvestmentItem'
          case AssetClassNames.CASH:
            return 'cashAccount'
          case AssetClassNames.PRECIOUS_METALS:
            return 'preciousMetalItem'
          default:
            return null
        }
      }

      /**
       * 
       * @param name a string 
       * @returns Formatted string. If the string length is greater than or equal to 5, it splits it using two new lines
       */
      formatLongNames = (name: string) => {
        const splitNames = name.split(" ");
        let wrappedWord = ''
        if(splitNames.length >= 5) {
          wrappedWord += splitNames.slice(0, splitNames.length/2).join(" ")
          wrappedWord += "\n \n"
          wrappedWord += splitNames.slice(splitNames.length/2).join(" ")
          
          return wrappedWord
         } else {
            return splitNames.join(" ");
         }
      };

      /**
       * @param locality the locality of the investment/security
       * @returns The sum of all security holdings in a locality.
       */
      getAssetClassTotalByLocality(locality: Locality): number {
            if(locality === Locality.Local)
                return this.localPorfolioValue
            else if(locality === Locality.International)
                return this.internationalPortfolioValue
            else
                return (this.localPorfolioValue + this.internationalPortfolioValue)
      }
}


// WEBPACK FOOTER //
// ./src/app/reporting/topholdings.ts